import React, { useState, useEffect, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Task Card Color Palette (from Figma design)
export const TASK_CARD_COLORS = {
  study: { bg: '#7C5CFC', light: '#A789FF' },      // Purple
  exercise: { bg: '#4ADE80', light: '#86EFAC' },   // Green  
  health: { bg: '#F87171', light: '#FCA5A5' },     // Red/Coral
  work: { bg: '#3B82F6', light: '#60A5FA' },       // Blue
  productivity: { bg: '#FACC15', light: '#FDE047' }, // Yellow/Amber
  learning: { bg: '#F472B6', light: '#F9A8D4' },   // Pink
};

// Theme context for global access
export const ThemeContext = createContext({
  isDarkMode: true,
  toggleTheme: () => { },
  showCelebration: () => { },
  showLevelUpAnimation: () => { },
});

// Named hook for convenient consumption
export const useTheme = () => useContext(ThemeContext);

const ThemeWrapper = ({ children }) => {
  // State for theme and animations
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const [levelUp, setLevelUp] = useState(null);

  // Initialize theme from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('idapps-theme');
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark');
    } else {
      // Use system preference as default
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(prefersDark);
    }
  }, []);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    localStorage.setItem('idapps-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Toggle theme with animation
  const toggleTheme = () => {
    // Start transition animation
    setIsTransitioning(true);

    // Toggle the theme after a small delay for smoother transition
    setTimeout(() => {
      setIsDarkMode(prev => !prev);

      // End transition animation after theme has changed
      setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
    }, 200);
  };

  // Show achievement celebration animation
  const showCelebration = (data = {}) => {
    setCelebration({
      title: data.title || 'Yeni Başarım!',
      description: data.description || 'Tebrikler, yeni bir başarım kazandın.',
      color: data.color || (isDarkMode ? '#00FFD1' : '#1D1AFF'),
      icon: data.icon,
      id: Date.now(), // Ensure we can trigger multiple celebrations
    });

    // Auto dismiss after 5 seconds
    setTimeout(() => {
      setCelebration(null);
    }, 5000);
  };

  // Show level up animation
  const showLevelUpAnimation = (data = {}) => {
    setLevelUp({
      level: data.level || 1,
      prevLevel: data.prevLevel || 0,
      reward: data.reward,
      message: data.message || 'Level atladın!',
      id: Date.now(),
    });

    // Auto dismiss after 6 seconds
    setTimeout(() => {
      setLevelUp(null);
    }, 6000);
  };

  // Generate particles for celebrations
  const renderParticles = (color = '#FFD700', count = 30) => {
    return Array.from({ length: count }).map((_, i) => (
      <motion.div
        key={`particle-${i}`}
        className="absolute rounded-full"
        style={{
          background: i % 3 === 0 ? color : i % 3 === 1 ? '#FFFFFF' : '#FFD700',
          width: Math.random() * 8 + 4,
          height: Math.random() * 8 + 4,
          top: '50%',
          left: '50%',
        }}
        initial={{
          x: 0,
          y: 0,
          opacity: 1
        }}
        animate={{
          x: (Math.random() - 0.5) * window.innerWidth * 0.5,
          y: (Math.random() - 0.5) * window.innerHeight * 0.5,
          opacity: 0,
          scale: Math.random() * 2
        }}
        transition={{
          duration: 2 + Math.random() * 2,
          ease: "easeOut"
        }}
      />
    ));
  };

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode,
        toggleTheme,
        showCelebration,
        showLevelUpAnimation
      }}
    >
      <div className={`relative min-h-screen w-full ${isDarkMode ? 'bg-slate-950 text-white' : 'bg-white text-slate-900'}`}>
        {/* Theme transition overlay */}
        <AnimatePresence>
          {isTransitioning && (
            <motion.div
              className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Central icon animation */}
              <motion.div
                className={`p-6 rounded-full ${isDarkMode ? 'bg-white' : 'bg-slate-950'}`}
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: [0, 1, 1.5, 0], rotate: 180 }}
                transition={{
                  duration: 1.2,
                  times: [0, 0.3, 0.7, 1],
                  ease: "easeInOut"
                }}
              >
                {isDarkMode ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-slate-900">
                    <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-white">
                    <path d="M21.0672 11.8568L20.4253 11.469L21.0672 11.8568ZM12.1432 2.93276L11.7553 2.29085V2.29085L12.1432 2.93276ZM21.25 12C21.25 17.1086 17.1086 21.25 12 21.25V22.75C17.9371 22.75 22.75 17.9371 22.75 12H21.25ZM12 21.25C6.89137 21.25 2.75 17.1086 2.75 12H1.25C1.25 17.9371 6.06294 22.75 12 22.75V21.25ZM2.75 12C2.75 6.89137 6.89137 2.75 12 2.75V1.25C6.06294 1.25 1.25 6.06294 1.25 12H2.75ZM15.5 14.25C12.3244 14.25 9.75 11.6756 9.75 8.5H8.25C8.25 12.5041 11.4959 15.75 15.5 15.75V14.25ZM20.4253 11.469C19.4172 13.1373 17.5882 14.25 15.5 14.25V15.75C18.1349 15.75 20.4407 14.3439 21.7092 12.2447L20.4253 11.469ZM9.75 8.5C9.75 6.41182 10.8627 4.5828 12.531 3.57467L11.7553 2.29085C9.65609 3.5593 8.25 5.86509 8.25 8.5H9.75ZM12 2.75C11.9115 2.75 11.8077 2.71008 11.7553 2.29085L12.531 3.57467C12.2099 3.38766 11.8615 3.25 11.5 3.25C11.9142 3.25 12.25 2.91421 12.25 2.5C12.25 2.08579 11.9142 1.75 11.5 1.75C11.1708 1.75 10.847 1.61725 10.651 1.40901C10.455 1.20078 10.25 0.857958 10.25 0.5H8.75C8.75 1.19036 9.09429 1.80526 9.48537 2.21853C9.87645 2.63179 10.371 2.91032 10.86 3.09477C11.1408 3.20573 11.3956 3.25 11.5 3.25C11.1005 3.25 10.7392 3.31431 10.4248 3.45687C10.0843 3.60983 9.96176 3.76005 9.85315 3.92683C9.64182 4.26948 9.75 4.83702 9.75 4.83702C9.75 4.83702 9.75 4.83655 9.75001 4.83493C9.75003 4.8333 9.75021 4.82841 9.75116 4.81862C9.75307 4.79902 9.75916 4.7587 9.77573 4.70233C9.80936 4.58823 9.89326 4.40837 10.0858 4.21592C10.2739 4.02789 10.5298 3.86736 10.843 3.7492C11.149 3.63334 11.5546 3.55971 12 3.55971V2.05971C11.4454 2.05971 10.851 2.14916 10.3258 2.34546C9.80842 2.53714 9.3511 2.82462 8.97568 3.2C8.60569 3.57096 8.31564 4.07427 8.22427 4.67267C8.17709 4.98203 8.18522 5.25733 8.2849 5.4935C8.37946 5.71966 8.54319 5.93802 8.83937 6.07442C9.4067 6.33419 10.2537 6.25 10.2537 6.25C10.6679 6.25 11.0037 5.91421 11.0037 5.5C11.0037 5.08579 11.3395 4.75 11.7537 4.75C11.7537 4.75 11.9992 4.75 12.1593 4.58579C12.2394 4.50308 12.25 4.44156 12.25 4.41604C12.25 4.39356 12.2487 4.39003 12.2475 4.38709C12.2462 4.38413 12.244 4.38038 12.24 4.37455C12.2321 4.3629 12.2153 4.34138 12.1817 4.31061C12.1145 4.24907 12.0411 4.21329 12 4.20233C12.0411 4.21329 12.1145 4.24907 12.1817 4.31061C12.315 4.4241 12.3857 4.58325 12.3984 4.75H13.8984C13.8984 4.55144 13.8531 4.33523 13.7197 4.14469C13.5862 3.95394 13.3979 3.83241 13.244 3.77517C13.09 3.71787 12.9481 3.72506 12.8701 3.7334C12.7963 3.74126 12.7367 3.75843 12.6711 3.77517C12.5399 3.8086 12.3769 3.86548 12.244 3.95144C12.1276 4.02984 11.9185 4.18032 11.7553 4.29085C11.9339 4.25164 12.0948 4.25 12.2181 4.25C12.3849 4.25 12.6412 4.27864 12.8983 4.41584C13.1554 4.55326 13.3984 4.83313 13.3984 5.25H14.8984C14.8984 4.41687 14.5164 3.69675 14.0548 3.25916C13.5932 2.82135 13.0401 2.6212 12.5966 2.56876C12.3996 2.5434 12.2032 2.75 12 2.75Z" fill="currentColor" />
                  </svg>
                )}
              </motion.div>

              {/* Background ripple effect */}
              <motion.div
                className={`absolute rounded-full ${isDarkMode ? 'bg-white' : 'bg-slate-950'}`}
                initial={{ scale: 0, opacity: 0.7 }}
                animate={{ scale: 10, opacity: 0 }}
                transition={{ duration: 1, ease: "easeOut" }}
              />

              {/* Particles spreading on theme change */}
              <div className="absolute inset-0 overflow-hidden">
                {Array.from({ length: 20 }).map((_, i) => (
                  <motion.div
                    key={i}
                    className={`absolute w-1 h-8 ${isDarkMode ? 'bg-white' : 'bg-slate-950'}`}
                    style={{
                      top: '50%',
                      left: '50%',
                      transformOrigin: 'bottom'
                    }}
                    initial={{
                      scale: 0,
                      rotate: i * 18 // spread in a circle
                    }}
                    animate={{
                      scale: [0, 1, 0],
                      opacity: [0, 0.7, 0]
                    }}
                    transition={{
                      duration: 0.8,
                      delay: i * 0.02
                    }}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Achievement celebration animation */}
        <AnimatePresence>
          {celebration && (
            <motion.div
              className="fixed inset-0 z-[9998] flex items-center justify-center pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              key={`celebration-${celebration.id}`}
            >
              {/* Background overlay */}
              <motion.div
                className="absolute inset-0 bg-black bg-opacity-60"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />

              {/* Central celebration card */}
              <motion.div
                className={`relative z-10 w-72 p-5 rounded-2xl shadow-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
                initial={{ scale: 0, rotate: -5 }}
                animate={{
                  scale: [0, 1.2, 1],
                  rotate: [-5, 5, 0]
                }}
                exit={{ scale: 0, rotate: 5, opacity: 0 }}
                transition={{
                  duration: 0.7,
                  ease: "backOut",
                  times: [0, 0.7, 1]
                }}
              >
                {/* Badge glow */}
                <motion.div
                  className="absolute inset-0 rounded-2xl"
                  style={{
                    boxShadow: `0 0 40px ${celebration.color}80`,
                    opacity: 0.6
                  }}
                  animate={{
                    opacity: [0.2, 0.6, 0.2]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />

                <div className="flex flex-col items-center">
                  <motion.div
                    className="w-20 h-20 rounded-full flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${celebration.color}20` }}
                    animate={{
                      scale: [1, 1.2, 1],
                      boxShadow: [
                        `0 0 0px ${celebration.color}40`,
                        `0 0 30px ${celebration.color}60`,
                        `0 0 0px ${celebration.color}40`
                      ]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  >
                    {celebration.icon || (
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={celebration.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                      </svg>
                    )}
                  </motion.div>

                  <motion.h3
                    className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
                    animate={{
                      scale: [1, 1.1, 1],
                      color: [null, celebration.color, null]
                    }}
                    transition={{ duration: 1.5, delay: 0.3 }}
                  >
                    {celebration.title}
                  </motion.h3>

                  <motion.p
                    className={`mt-2 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    {celebration.description}
                  </motion.p>
                </div>
              </motion.div>

              {/* Particle effects */}
              <div className="absolute inset-0 overflow-hidden">
                {renderParticles(celebration.color)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Level Up animation */}
        <AnimatePresence>
          {levelUp && (
            <motion.div
              className="fixed inset-0 z-[9997] flex items-center justify-center pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              key={`levelup-${levelUp.id}`}
            >
              {/* Background overlay with light rays */}
              <motion.div
                className="absolute inset-0 bg-black bg-opacity-70"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />

              {/* Rays of light radiating outwards */}
              {Array.from({ length: 12 }).map((_, i) => (
                <motion.div
                  key={`ray-${i}`}
                  className="absolute top-1/2 left-1/2 origin-center"
                  style={{
                    width: '5px',
                    height: '100vh',
                    background: `linear-gradient(to top, transparent, ${isDarkMode ? 'rgba(0, 255, 209, 0.4)' : 'rgba(29, 26, 255, 0.3)'}, transparent)`,
                    rotate: `${i * 30}deg`
                  }}
                  initial={{ scaleY: 0, opacity: 0 }}
                  animate={{
                    scaleY: [0, 1, 1, 0],
                    opacity: [0, 0.7, 0.7, 0]
                  }}
                  transition={{
                    duration: 3,
                    times: [0, 0.3, 0.7, 1],
                    delay: 0.2,
                  }}
                />
              ))}

              {/* Central level up content */}
              <div className="relative z-10 flex flex-col items-center">
                {/* Previous level indicator (animating out) */}
                <motion.div
                  className="absolute"
                  initial={{ scale: 1, y: 0, opacity: 1 }}
                  animate={{ scale: 1.5, y: -100, opacity: 0 }}
                  transition={{ duration: 1 }}
                >
                  <div className={`text-6xl font-bold ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    LEVEL {levelUp.prevLevel}
                  </div>
                </motion.div>

                {/* LEVEL UP text */}
                <motion.h2
                  className="text-2xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600"
                  initial={{ scale: 0 }}
                  animate={{
                    scale: [0, 1.2, 1],
                    y: [0, -20, -20]
                  }}
                  transition={{
                    duration: 0.8,
                    delay: 1,
                    times: [0, 0.7, 1]
                  }}
                >
                  LEVEL UP!
                </motion.h2>

                {/* New level display */}
                <motion.div
                  className="relative"
                  initial={{ scale: 0 }}
                  animate={{ scale: [0, 1.3, 1] }}
                  transition={{
                    duration: 1,
                    delay: 1.5,
                    times: [0, 0.6, 1],
                    ease: "backOut"
                  }}
                >
                  {/* Pulsing rings */}
                  {[1, 2, 3].map((ring) => (
                    <motion.div
                      key={`ring-${ring}`}
                      className="absolute rounded-full"
                      style={{
                        inset: 0,
                        border: `${ring * 2}px solid ${isDarkMode ? '#00FFD1' : '#1D1AFF'}`,
                        opacity: 0.2,
                        margin: `-${ring * 15}px`,
                      }}
                      animate={{
                        opacity: [0, 0.2, 0],
                        scale: [0.8, 1, 1.2],
                      }}
                      transition={{
                        duration: 2,
                        delay: 1.8 + ring * 0.3,
                        repeat: 1,
                        repeatDelay: 1
                      }}
                    />
                  ))}

                  {/* Level badge */}
                  <div
                    className={`w-32 h-32 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'
                      }`}
                    style={{
                      boxShadow: `0 0 40px ${isDarkMode ? 'rgba(0, 255, 209, 0.6)' : 'rgba(29, 26, 255, 0.4)'}`
                    }}
                  >
                    <span className={`text-7xl font-bold ${isDarkMode
                        ? 'bg-clip-text text-transparent bg-gradient-to-br from-teal-400 to-blue-400'
                        : 'bg-clip-text text-transparent bg-gradient-to-br from-blue-600 to-indigo-600'
                      }`}>
                      {levelUp.level}
                    </span>
                  </div>
                </motion.div>

                {/* Level message */}
                <motion.p
                  className={`mt-8 text-xl ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2.3 }}
                >
                  {levelUp.message}
                </motion.p>

                {/* Reward display if any */}
                {levelUp.reward && (
                  <motion.div
                    className={`mt-4 px-4 py-2 rounded-lg ${isDarkMode ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700'
                      } flex items-center`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2.6 }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="mr-2">
                      <path d="M20.25 7.5H3.75M20.25 7.5C20.6642 7.5 21 7.83579 21 8.25V11.25C21 11.6642 20.6642 12 20.25 12H3.75C3.33579 12 3 11.6642 3 11.25V8.25C3 7.83579 3.33579 7.5 3.75 7.5M20.25 7.5V6.75C20.25 5.50736 19.2426 4.5 18 4.5C16.7574 4.5 15.75 5.50736 15.75 6.75V7.5M3.75 7.5V6.75C3.75 5.50736 4.75736 4.5 6 4.5C7.24264 4.5 8.25 5.50736 8.25 6.75V7.5M3.75 12V19.5C3.75 19.9142 4.08579 20.25 4.5 20.25H19.5C19.9142 20.25 20.25 19.9142 20.25 19.5V12M12 7.5H8.25M12 7.5H15.75"
                        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>{levelUp.reward}</span>
                  </motion.div>
                )}
              </div>

              {/* Particle effects */}
              <div className="absolute inset-0 overflow-hidden">
                {renderParticles(isDarkMode ? '#00FFD1' : '#1D1AFF', 40)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export default ThemeWrapper;